import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { cn } from '../lib/utils';
import {
  Check,
  CreditCard,
  Shield,
  Clock,
  HelpCircle,
  Loader2
} from 'lucide-react';

interface Plan {
  id: string;
  name: string;
  amount: number;
  currency: string;
  interval: 'month' | 'year';
  features: string[];
}

type PaymentProvider = 'stripe' | 'shopify' | 'razorpay';

const Pricing: React.FC = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState<PaymentProvider>('stripe');
  const [availableProviders, setAvailableProviders] = useState<PaymentProvider[]>([]);
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);

  useEffect(() => {
    loadPlans();
  }, [selectedProvider]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const userCountry = await detectUserCountry();

      const response = await apiService.get('/billing/plans', {
        params: {
          country: userCountry,
          isShopify: false,
        },
      });

      setPlans(response.data.plans || []);
      setAvailableProviders(response.data.availableProviders || ['stripe']);
      setSelectedProvider(response.data.selectedGateway || 'stripe');
    } catch (error) {
      console.error('Failed to load plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const detectUserCountry = async (): Promise<string> => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      return data.country_code || 'US';
    } catch (error) {
      return 'US';
    }
  };

  const handleSelectPlan = async (planId: string) => {
    try {
      setProcessingPlan(planId);

      const response = await apiService.post('/billing/checkout', {
        planId,
        provider: selectedProvider,
        trialDays: 14,
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error: any) {
      console.error('Failed to create checkout:', error);
      if (error.response?.status === 401) {
        navigate('/login?redirect=/pricing');
      } else {
        alert('Failed to start checkout. Please try again.');
      }
    } finally {
      setProcessingPlan(null);
    }
  };

  const formatPrice = (amount: number, currency: string) => {
    if (currency === 'INR') {
      return `₹${amount.toLocaleString('en-IN')}`;
    }
    return `$${amount}`;
  };

  const getProviderName = (provider: PaymentProvider): string => {
    switch (provider) {
      case 'stripe':
        return 'Stripe (Credit Card)';
      case 'razorpay':
        return 'Razorpay (UPI, Cards, Net Banking)';
      case 'shopify':
        return 'Shopify Billing';
      default:
        return provider;
    }
  };

  const getProviderIcon = (provider: PaymentProvider) => {
    switch (provider) {
      case 'stripe':
        return <CreditCard className="h-6 w-6" />;
      case 'razorpay':
        return <span className="text-xl">🇮🇳</span>;
      case 'shopify':
        return <span className="text-xl">🛍️</span>;
      default:
        return <CreditCard className="h-6 w-6" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Skeleton className="h-10 w-64 mx-auto mb-4" />
            <Skeleton className="h-6 w-96 mx-auto" />
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="pt-8">
                  <Skeleton className="h-8 w-24 mb-4" />
                  <Skeleton className="h-12 w-32 mb-6" />
                  <div className="space-y-3">
                    {[1, 2, 3, 4].map((j) => (
                      <Skeleton key={j} className="h-4 w-full" />
                    ))}
                  </div>
                  <Skeleton className="h-12 w-full mt-8" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Start with a 14-day free trial. No credit card required.
          </p>
        </div>

        {/* Payment Method Selector */}
        {availableProviders.length > 1 && (
          <div className="max-w-2xl mx-auto mb-12">
            <p className="block text-sm font-semibold text-foreground mb-3 text-center">
              Select Payment Method
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {availableProviders.map((provider) => (
                <Button
                  key={provider}
                  variant={selectedProvider === provider ? 'default' : 'outline'}
                  className={cn(
                    "h-auto py-4 flex flex-col items-center gap-2",
                    selectedProvider === provider && "ring-2 ring-primary"
                  )}
                  onClick={() => setSelectedProvider(provider)}
                >
                  {getProviderIcon(provider)}
                  <span className="text-sm font-medium">
                    {getProviderName(provider)}
                  </span>
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => {
            const isPopular = plan.name === 'Pro';
            const isEnterprise = plan.name === 'Enterprise';

            return (
              <Card
                key={plan.id}
                className={cn(
                  "relative overflow-hidden",
                  isPopular && "ring-2 ring-primary scale-105 shadow-lg"
                )}
              >
                {isPopular && (
                  <div className="absolute top-0 left-0 right-0 bg-primary text-primary-foreground text-center py-2 font-semibold text-sm">
                    MOST POPULAR
                  </div>
                )}

                <CardContent className={cn("pt-8", isPopular && "pt-14")}>
                  {/* Plan Name */}
                  <h3 className="text-2xl font-bold text-foreground mb-2">
                    {plan.name}
                  </h3>

                  {/* Price */}
                  <div className="mb-6">
                    <span className="text-5xl font-bold text-foreground">
                      {formatPrice(plan.amount, plan.currency)}
                    </span>
                    <span className="text-muted-foreground ml-2">/month</span>
                  </div>

                  {/* Trial Info */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
                    <p className="text-sm text-green-800 font-medium flex items-center">
                      <Check className="h-4 w-4 mr-2" />
                      14-day free trial included
                    </p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button
                    onClick={() => handleSelectPlan(plan.id)}
                    disabled={processingPlan === plan.id}
                    className="w-full"
                    variant={isPopular || isEnterprise ? 'default' : 'outline'}
                  >
                    {processingPlan === plan.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Start Free Trial'
                    )}
                  </Button>

                  <p className="text-xs text-muted-foreground text-center mt-3">
                    No credit card required
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Trust Badges */}
        <div className="mt-16 text-center">
          <p className="text-sm text-muted-foreground mb-4">Trusted by 500+ businesses worldwide</p>
          <div className="flex justify-center items-center flex-wrap gap-8">
            <div className="flex items-center space-x-2">
              <Shield className="h-5 w-5 text-green-500" />
              <span className="text-sm text-foreground">Secure Payments</span>
            </div>
            <div className="flex items-center space-x-2">
              <Check className="h-5 w-5 text-green-500" />
              <span className="text-sm text-foreground">Cancel Anytime</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-green-500" />
              <span className="text-sm text-foreground">24/7 Support</span>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-foreground mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center">
                  <HelpCircle className="h-5 w-5 mr-2 text-primary" />
                  How does the free trial work?
                </h3>
                <p className="text-muted-foreground">
                  You get full access to all features for 14 days. No credit card required to start.
                  You'll only be charged after the trial ends if you choose to continue.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center">
                  <HelpCircle className="h-5 w-5 mr-2 text-primary" />
                  Can I change plans later?
                </h3>
                <p className="text-muted-foreground">
                  Yes! You can upgrade or downgrade your plan at any time. Changes take effect
                  immediately, and we'll prorate the charges.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center">
                  <HelpCircle className="h-5 w-5 mr-2 text-primary" />
                  What payment methods do you accept?
                </h3>
                <p className="text-muted-foreground">
                  We accept credit cards (via Stripe), UPI/Net Banking/Wallets (via Razorpay for
                  India), and Shopify merchants can use Shopify Billing.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
